/**
 * @file TimeoutModal.tsx
 * @description Inactivity session-expiry warning modal.
 * Shows a countdown timer; user can continue or logout.
 * Design matches AdminMissingPart7 reference.
 */
import { useState, useEffect } from 'react';

/** Role display config for personalising the modal header. */
const ROLE_CONFIG: Record<string, { label: string; gradient: string; icon: string; sessionLabel: string }> = {
  ADMIN: {
    label: 'Admin Portal',
    gradient: 'linear-gradient(135deg,#4F46E5,#7C3AED)',
    icon: '🏫',
    sessionLabel: '30-min session',
  },
  SUPER_ADMIN: {
    label: 'Super Admin',
    gradient: 'linear-gradient(135deg,#4F46E5,#7C3AED)',
    icon: '🔐',
    sessionLabel: '30-min session',
  },
  EMPLOYEE: {
    label: 'Employee Portal',
    gradient: 'linear-gradient(135deg,#0D9488,#0891B2)',
    icon: '👤',
    sessionLabel: '60-min session',
  },
  STUDENT: {
    label: 'Student Portal',
    gradient: 'linear-gradient(135deg,#059669,#0D9488)',
    icon: '🎓',
    sessionLabel: '60-min session',
  },
  APP_OWNER: {
    label: 'Owner Portal',
    gradient: 'linear-gradient(135deg,#D97706,#DC2626)',
    icon: '⚙️',
    sessionLabel: '15-min session',
  },
};

interface TimeoutModalProps {
  /** The user's role string (e.g. 'ADMIN') */
  role: string;
  /** Called when user clicks "Continue Working" — resets the timer */
  onContinue: () => void;
  /** Called when user clicks "Logout Now" or countdown hits 0 */
  onLogout: () => void;
  /** Initial countdown seconds (default: 120) */
  initialSeconds?: number;
}

/**
 * TimeoutModal
 * Displays a countdown warning before auto-logout.
 */
export default function TimeoutModal({
  role,
  onContinue,
  onLogout,
  initialSeconds = 120,
}: TimeoutModalProps) {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  const cfg = ROLE_CONFIG[role] ?? ROLE_CONFIG['ADMIN'];
  const urgent = secondsLeft <= 30;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  useEffect(() => {
    if (secondsLeft <= 0) {
      onLogout();
      return;
    }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft, onLogout]);

  return (
    /* Overlay */
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(13,11,30,0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 'var(--z-modal)' as unknown as number,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      {/* Modal card */}
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: 360,
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)',
          animation: 'scaleIn 0.25s ease',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: cfg.gradient,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 20 }}>{cfg.icon}</span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 800,
                fontSize: 15,
                color: '#fff',
              }}
            >
              Session Expiring
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
              {cfg.sessionLabel}
            </div>
          </div>
          <span
            style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 20,
              padding: '2px 10px',
              fontSize: 11,
              fontWeight: 700,
              color: '#fff',
            }}
          >
            {cfg.label}
          </span>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          <p
            style={{
              fontSize: 13,
              color: 'var(--t2)',
              fontWeight: 500,
              lineHeight: 1.6,
              marginBottom: 16,
            }}
          >
            You've been inactive. Your session will expire in:
          </p>

          {/* Countdown display */}
          <div
            style={{
              background: urgent ? 'var(--red-lt)' : 'var(--p-lt)',
              border: `1.5px solid ${urgent ? '#FCA5A5' : 'var(--p-md)'}`,
              borderRadius: 16,
              padding: '16px 20px',
              textAlign: 'center',
              marginBottom: 20,
              transition: 'all 0.4s ease',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 52,
                fontWeight: 800,
                color: urgent ? 'var(--red)' : 'var(--p)',
                letterSpacing: '-2px',
                lineHeight: 1,
                animation: urgent ? 'pulse 0.8s ease infinite' : 'none',
              }}
            >
              {mm}:{ss}
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--t3)',
                fontWeight: 600,
                marginTop: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {urgent ? '⚠️ SESSION EXPIRING SOON' : 'MINUTES : SECONDS'}
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={onContinue}
              style={{
                background: 'linear-gradient(135deg,#4F46E5,#7C3AED)',
                color: '#fff',
                border: 'none',
                borderRadius: 11,
                height: 44,
                fontWeight: 700,
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                cursor: 'pointer',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                minHeight: 44,
              }}
            >
              ✅ Continue Working
            </button>
            <button
              onClick={onLogout}
              style={{
                background: 'transparent',
                color: 'var(--red)',
                border: '1.5px solid var(--red)',
                borderRadius: 11,
                height: 44,
                fontWeight: 700,
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                cursor: 'pointer',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                minHeight: 44,
              }}
            >
              🚪 Logout Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}